/**
 * lockReconciliation.ts — what a lock report MEANS, kept separate from what it says.
 *
 * The rules this file enforces, and why each is structural rather than a habit:
 *
 *   1. A lock is recognised BY SHAPE. A lock is an identifier plus a domain plus
 *      a subject — that is what a lock IS — so this reader recognises that and
 *      nothing else. It holds no vocabulary of lock domains, because a domain this
 *      browser has never heard of is still a domain, and rendering it as its own
 *      text is the correct outcome where guessing would not be.
 *   2. SCHEMATIC AND PHYSICAL LOCKS ARE NOT MERGED. They are the same SHAPE and
 *      they are emphatically not the same FACT: a lock on a manual placement in a
 *      panel is in millimetres in a real assembly, and a lock on a route on a sheet
 *      is in a drawing. A view that merged them would let a reviewer read a
 *      schematic lock as though it protected a physical coordinate — which is the
 *      exact confusion P48's fixture is built to detect, and the one that makes a
 *      physical assembly untrustworthy. So the domain is carried through verbatim
 *      and displayed as its own grouping, and nothing here decides which frame a
 *      lock governs. The Engine owns that; the browser cannot.
 *   3. AN UNRESOLVED LOCK BLOCKS. Apply stays unavailable while any reported lock
 *      carries no resolution of its own. There is no "probably fine" and no
 *      majority rule.
 *   4. NO RELEASE, FORCE OR OVERRIDE CONTROL EXISTS IN THIS REPOSITORY. Not as a
 *      disabled button, not behind a flag: unrepresentable. See
 *      `lockPanelHoldsNoOverrideControl` in the test suite, which fails if a
 *      verb like that ever appears in the component.
 */

export interface ReportedLock {
  readonly lockId: string;
  /** The Engine's own domain text, verbatim. Never mapped to a frame. */
  readonly domain: string;
  readonly subjectId: string;
  readonly reason?: string;
  /** The Engine's own resolution for this lock, when it published one. */
  readonly resolution?: string;
  /**
   * Where in the Engine's answer this lock was found.
   *
   * Reported because "a lock exists" and "a lock exists in the regeneration
   * report" are different claims, and a reviewer reconciling a regeneration needs
   * to know which section it came from.
   */
  readonly path: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function textOf(record: Record<string, unknown>, name: string): string | undefined {
  const value = record[name];
  return typeof value === "string" && value !== "" ? value : undefined;
}

export interface LockReport {
  readonly locks: readonly ReportedLock[];
  /**
   * The Engine's own refusal text about locks, verbatim.
   *
   * A refusal this browser cannot parse is still a refusal. Dropping it would
   * make a conflict the Engine described disappear from the screen, which is the
   * one outcome a reconciliation panel exists to prevent.
   */
  readonly refusals: readonly string[];
}

/**
 * Pull the lock reports out of an Engine result, if it published any.
 *
 * Bounded in depth, so a hostile or accidental cycle cannot hang the workbench.
 * The bound is a display limit, not a semantic one: a lock nested deeper than the
 * bound is not reported, and that is stated rather than hidden.
 */
export function readLocks(result: unknown, maxDepth = 6): LockReport {
  const locks: ReportedLock[] = [];
  const refusals: string[] = [];
  const visit = (node: unknown, depth: number, path: string): void => {
    if (depth > maxDepth) return;
    if (typeof node === "string") {
      // A bare string the Engine wrote ABOUT a lock reaches the reviewer as
      // text. Matching on the word "lock" is not a lock vocabulary — it is how a
      // refusal this browser cannot structurally parse is prevented from vanishing.
      if (/\block\b/i.test(node)) refusals.push(node);
      return;
    }
    if (!isRecord(node)) return;
    const lockId = textOf(node, "lockId");
    const domain = textOf(node, "domain");
    const subjectId = textOf(node, "subjectId");
    if (lockId !== undefined && domain !== undefined && subjectId !== undefined) {
      const reason = textOf(node, "reason");
      const resolution = textOf(node, "resolution");
      locks.push({
        lockId,
        domain,
        subjectId,
        path,
        ...(reason !== undefined ? { reason } : {}),
        ...(resolution !== undefined ? { resolution } : {}),
      });
    }
    for (const [key, value] of Object.entries(node)) {
      if (Array.isArray(value)) value.forEach((entry, index) => visit(entry, depth + 1, `${path}.${key}[${index}]`));
      else visit(value, depth + 1, `${path}.${key}`);
    }
  };
  visit(result, 0, "$");
  const unique = new Map(locks.map(lock => [`${lock.path}:${lock.lockId}`, lock]));
  return { locks: [...unique.values()].sort((a, b) => a.lockId.localeCompare(b.lockId)), refusals: [...new Set(refusals)].sort() };
}

export interface LockVerdict {
  /** Locks the Engine reported and that nobody has resolved. */
  readonly unresolved: readonly ReportedLock[];
  /** Locks the Engine already resolved. Shown, never hidden. */
  readonly resolved: readonly ReportedLock[];
  /**
   * The distinct domains the Engine reported, verbatim and unsorted-into-meaning.
   *
   * Grouping happens in the UI and is presentation only: two locks in different
   * domains are never counted as one, and this browser does not say which frame
   * either of them governs.
   */
  readonly domains: readonly string[];
  readonly blocksApply: boolean;
  readonly refusals: readonly string[];
  /**
   * Whether the Engine reported any lock-bearing structure at all.
   *
   * FALSE is NOT a statement that nothing is locked. It is a statement about this
   * result, and the UI is required to say so, because a panel that renders
   * "nothing is locked" from an absence would be making a claim about protected
   * engineering geometry that the Engine never made.
   */
  readonly reported: boolean;
}

export function lockVerdict(result: unknown): LockVerdict {
  const { locks, refusals } = readLocks(result);
  const resolved = locks.filter(lock => lock.resolution !== undefined);
  const unresolved = locks.filter(lock => lock.resolution === undefined);
  return {
    unresolved,
    resolved,
    domains: [...new Set(locks.map(lock => lock.domain))].sort(),
    blocksApply: unresolved.length > 0,
    refusals,
    reported: locks.length > 0 || refusals.length > 0,
  };
}
